import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

import {
  FaBrain,
  FaGamepad,
  FaChess,
  FaPuzzlePiece,
  FaKeyboard,
  FaCalculator,
  FaListOl,
  FaBug,
  FaBomb,
  FaSun,
  FaMoon,
  FaBolt,
  FaHeadphones,
} from "react-icons/fa";

import "./Home.css";

const THEMES = {
  NEON: "neon",
  DARK: "dark",
  LIGHT: "light",
};

const CATEGORY_META = [
  { key: "all", label: "All", emoji: "🎮" },
  { key: "strategy", label: "Strategy", emoji: "♟️" },
  { key: "arcade", label: "Arcade", emoji: "👾" },
  { key: "brain", label: "Brain", emoji: "🧠" },
  { key: "typing", label: "Typing", emoji: "⌨️" },
  { key: "math", label: "Math", emoji: "🔢" },
];

const DEFAULT_GAMES = [
  {
    name: "Tic Tac Toe",
    path: "/tictactoe",
    icon: <FaChess />,
    desc: "Classic strategy game against an AI or friend. Try different difficulty levels.",
    category: "strategy",
    difficulty: "Easy",
    aliases: ["xo", "tic", "tictactoe", "tic tac toe"],
  },
  {
    name: "Snake",
    path: "/snake",
    icon: <FaGamepad />,
    desc: "Retro snake with smooth controls and progressive speed.",
    category: "arcade",
    difficulty: "Medium",
    aliases: ["snake", "slither", "retro snake"],
  },
  {
    name: "Memory Card",
    path: "/memory",
    icon: <FaBrain />,
    desc: "Flip cards and match pairs — trains short-term memory.",
    category: "brain",
    difficulty: "Medium",
    aliases: ["memory", "pairs", "card match", "memory card"],
  },
  {
    name: "Sudoku",
    path: "/sudoku",
    icon: <FaPuzzlePiece />,
    desc: "Random Sudoku puzzles with multiple difficulty levels.",
    category: "brain",
    difficulty: "Hard",
    aliases: ["sudoku", "number puzzle"],
  },
  {
    name: "Flappy Bird",
    path: "/flappyBird",
    icon: <FaGamepad />,
    desc: "Fly through gaps and chase the high score.",
    category: "arcade",
    difficulty: "Hard",
    aliases: ["flappy", "flappy bird"],
  },
  {
    name: "Coding Puzzle",
    path: "/codingPuzzle",
    icon: <FaBug />,
    desc: "Solve logic and coding-style puzzles (no coding required).",
    category: "brain",
    difficulty: "Medium",
    aliases: ["coding", "puzzle", "code puzzle"],
  },
  {
    name: "Minesweeper",
    path: "/MinesWeeper",
    icon: <FaBomb />,
    desc: "Avoid hidden mines using logic clues.",
    category: "strategy",
    difficulty: "Hard",
    aliases: ["minesweeper", "mines"],
  },
  {
    name: "Typing Challenge",
    path: "/TypingChallenge",
    icon: <FaKeyboard />,
    desc: "Improve typing speed and accuracy with timed tests.",
    category: "typing",
    difficulty: "Easy",
    aliases: ["typing", "type test", "typing challenge"],
  },
  {
    name: "Advanced Math Quiz",
    path: "/AdvancedMathQuiz",
    icon: <FaCalculator />,
    desc: "Math quizzes with adaptive difficulty and levels.",
    category: "math",
    difficulty: "Medium",
    aliases: ["math", "math quiz", "advanced math"],
  },
  {
    name: "Number Sequence Game",
    path: "/NumberSequenceGame",
    icon: <FaListOl />,
    desc: "Predict the next term in numeric sequences.",
    category: "math",
    difficulty: "Medium",
    aliases: ["sequence", "number sequence", "pattern game"],
  },
];

// Local storage helpers for best scores
function readBestScore(gameKey) {
  try {
    const x = localStorage.getItem(`ss_best_${gameKey}`);
    return x ? JSON.parse(x) : { name: null, score: 0, plays: 0 };
  } catch {
    return { name: null, score: 0, plays: 0 };
  }
}

function saveBestScore(gameKey, record) {
  try {
    localStorage.setItem(`ss_best_${gameKey}`, JSON.stringify(record));
  } catch {}
}

// Lightweight click sound
function playUIClick(enabled) {
  if (!enabled) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.value = 700;
    g.gain.value = 0.04;
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    setTimeout(() => { o.stop(); ctx.close(); }, 80);
  } catch {}
}

// ------------------ Extra Sections ------------------
function IntroSection() {
  return (
    <section className="intro-section">
      <h2>Welcome to SamSoftix GameHub!</h2>
      <p>
        Dive into our collection of neon-style mini-games, designed for fun, challenge, and brain training.
        Whether you love classic arcade action, strategy battles, or testing your typing and math skills,
        GameHub has something for everyone. Play instantly, no downloads required, and enjoy a seamless
        experience on desktop or mobile.
      </p>
      <p>
        Keep track of your high scores, challenge your friends, and explore new games every week.
        Use the search bar above or filter by category to find your favorite games quickly.
      </p>
    </section>
  );
}

function HowToPlay() {
  return (
    <section className="how-to-play">
      <h2>How to Play</h2>
      <ul>
        <li>Click on any game box to start playing instantly.</li>
        <li>Use keyboard shortcuts like <strong>Tab</strong> to navigate and <strong>Enter</strong> to select.</li>
        <li>Hover over games to see a quick preview of description, difficulty, and leaderboard stats.</li>
        <li>Switch between <strong>Neon</strong>, <strong>Dark</strong>, and <strong>Light</strong> themes using the buttons above.</li>
        <li>Toggle game sounds to enhance your experience or keep it silent.</li>
      </ul>
    </section>
  );
}

function FeaturedGames() {
  return (
    <section className="featured-games">
      <h2>Featured Games</h2>
      <p>Here are some of our most popular and highly-rated games by the GameHub community:</p>
      <ul>
        <li><strong>Tic Tac Toe:</strong> Classic strategy fun for all ages.</li>
        <li><strong>Snake:</strong> Retro gaming with smooth controls and speed challenges.</li>
        <li><strong>Sudoku:</strong> Sharpen your brain with puzzles of various difficulties.</li>
        <li><strong>Typing Challenge:</strong> Boost your typing speed and accuracy.</li>
        <li><strong>Advanced Math Quiz:</strong> Test your number skills with adaptive difficulty.</li>
      </ul>
    </section>
  );
}

function TipsSection() {
  return (
    <section className="tips-section">
      <h2>Tips & Tricks</h2>
      <p>Want to improve your scores? Here are some tips:</p>
      <ol>
        <li>Practice regularly — consistency helps in reflex-based games like Snake or Flappy Bird.</li>
        <li>Take small breaks to avoid fatigue in strategy and brain games.</li>
        <li>Focus on accuracy first, then speed, especially in Typing and Math games.</li>
        <li>Check the high scores for each game to see strategies used by top players.</li>
        <li>Experiment with different difficulty levels to find your sweet spot.</li>
      </ol>
    </section>
  );
}

function WhyGameHub() {
  return (
    <section className="why-gamehub">
      <h2>Why Choose SamSoftix GameHub?</h2>
      <p>SamSoftix GameHub is built with care for gamers of all ages. Here’s why it’s unique:</p>
      <ul>
        <li>Fast and responsive gameplay on any device.</li>
        <li>No downloads or installation — start instantly in your browser.</li>
        <li>Neon cyber-futuristic design for an immersive experience.</li>
        <li>High-score tracking to challenge yourself and friends.</li>
        <li>Regularly updated games and new challenges.</li>
      </ul>
    </section>
  );
}

// ------------------ Main Home Component ------------------
export default function Home() {
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("all");
  const [theme, setTheme] = useState(() => localStorage.getItem("ss_theme") || THEMES.NEON);
  const [soundOn, setSoundOn] = useState(() => (localStorage.getItem("ss_sound") || "1") === "1");
  const [hoverPreview, setHoverPreview] = useState(null);
  const games = useMemo(() => DEFAULT_GAMES, []);
  const rootRef = useRef();

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("ss_theme", theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem("ss_sound", soundOn ? "1" : "0");
  }, [soundOn]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return games.filter((g) => {
      if (category !== "all" && g.category !== category) return false;
      if (!q) return true;
      const tokens = q.split(/\s+/).filter(Boolean);
      const hay = `${g.name} ${g.aliases?.join(" ")} ${g.desc}`.toLowerCase();
      return tokens.every((t) => hay.includes(t) || g.name.toLowerCase().startsWith(t) || g.aliases.some(a => a.includes(t)));
    });
  }, [searchTerm, games, category]);

  function handleThemeChange(t) { playUIClick(soundOn); setTheme(t); }
  function handleSoundToggle() { setSoundOn(s => { const next = !s; playUIClick(next); return next; }); }
  function onCardMouseEnter(e, game) {
    const rect = e.currentTarget.getBoundingClientRect();
    setHoverPreview({ game, coords: { x: rect.right + 12, y: rect.top + window.scrollY + 12 } });
  }
  function onCardMouseLeave() { setHoverPreview(null); }
  function getBest(game) { return readBestScore(game.name.replace(/\s+/g, "_").toLowerCase()); }
  function onCardKeyDown(e, path) { if(e.key==="Enter"||e.key===" "){playUIClick(soundOn);window.location.href=path;} }
  function handleCategorySelect(key){ playUIClick(soundOn); setCategory(key); }

  return (
    <div className="home-wrapper root" ref={rootRef}>
      <Helmet>
  <title>SamSoftix GameHub | Neon Mini Games, Arcade, Brain, Typing & Math</title>
  <meta name="description" content="Play neon-style mini-games online: Tic Tac Toe, Snake, Sudoku, Typing Challenge, Math quizzes and more. Fun, free, browser-based games for kids, teens, and adults. Challenge your brain, improve typing speed, and enjoy classic arcade action."/>
  <meta name="keywords" content="SamSoftix, GameHub, browser games, online games, mini games, Qasim Mehmood Cheema,Qasim Cheema,Sambrial Software House,Game Development Sambrial,Samsoftix Products,samsoftix,samsoftix products,qasim cheema,software house in sambrial,computer courses in sambrial,website development projects,game based web project,web development,computer courses,computer courses samsoftix,sambrial,moon plaza,sialkot international airport ,neon games, arcade games, brain games, memory games, typing challenge, math quiz, tic tac toe, snake, sudoku, flappy bird, coding puzzle, minesweeper, number sequence, improve typing, improve memory, cognitive games, logic games, free games, no download games"/>
  <link rel="canonical" href="https://samsoftix.com/gamehub"/>
  <meta property="og:title" content="SamSoftix GameHub | Play Neon Mini Games Online"/>
  <meta property="og:description" content="Enjoy free browser games like Snake, Tic Tac Toe, Sudoku, Typing Challenge, and more. Fun, responsive, and instant gameplay for all ages."/>
  <meta property="og:type" content="website"/>
  <meta property="og:url" content="https://samsoftix.com/gamehub"/>
  <meta property="og:image" content="https://samsoftix.com/assets/gamehub-preview.png"/>
  <meta name="twitter:card" content="summary_large_image"/>
  <meta name="twitter:title" content="SamSoftix GameHub | Neon Mini Games Online"/>
  <meta name="twitter:description" content="Play free mini-games online. Improve typing speed, brain skills, and enjoy arcade classics instantly."/>
  <meta name="twitter:image" content="https://samsoftix.com/assets/gamehub-preview.png"/>
</Helmet>

      {/* Background */}
      <div className="stars" aria-hidden />
      <div className="stars2" aria-hidden />
      <div className="stars3" aria-hidden />
      <div className="shooting-star" aria-hidden />
      <div className="nebula" aria-hidden />

      {/* Header */}
      <header className="hero-section header-top">
        <div className="brand-wrap">
          <h1 className="brand-title" aria-label="SamSoftix GameHub"><span className="glow-text">SAMSOFTIX</span> GAMEHUB</h1>
          <p className="tagline">Neon Cyber-Futuristic Mini Games — Play Instantly</p>
        </div>
        <div className="controls">
          <div className="theme-switch" role="radiogroup" aria-label="Theme selector">
            <button className={`theme-btn ${theme===THEMES.NEON?"active":""}`} aria-checked={theme===THEMES.NEON} onClick={()=>handleThemeChange(THEMES.NEON)} title="Neon theme"><FaBolt aria-hidden/> Neon</button>
            <button className={`theme-btn ${theme===THEMES.DARK?"active":""}`} aria-checked={theme===THEMES.DARK} onClick={()=>handleThemeChange(THEMES.DARK)} title="Dark theme"><FaMoon aria-hidden/> Dark</button>
            <button className={`theme-btn ${theme===THEMES.LIGHT?"active":""}`} aria-checked={theme===THEMES.LIGHT} onClick={()=>handleThemeChange(THEMES.LIGHT)} title="Light theme"><FaSun aria-hidden/> Light</button>
          </div>
          <button className={`sound-toggle ${soundOn?"on":"off"}`} onClick={handleSoundToggle} aria-pressed={soundOn} title={soundOn?"Turn sound off":"Turn sound on"}><FaHeadphones/> {soundOn?"Sound On":"Sound Off"}</button>
        </div>
      </header>

      {/* Search + Categories */}
      <div className="search-and-cats">
        <div className="search-container">
          <input type="search" placeholder="Search games, e.g. 'snake', 'xo', 'typing'..." value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} aria-label="Search games"/>
        </div>
        <div className="categories" role="tablist" aria-label="Game categories">
          {CATEGORY_META.map(c=>(<button key={c.key} className={`cat-btn ${category===c.key?"active":""}`} onClick={()=>handleCategorySelect(c.key)} aria-selected={category===c.key}><span className="cat-emoji" aria-hidden>{c.emoji}</span> {c.label}</button>))}
        </div>
      </div>

      {/* Welcome text at top */}
      <IntroSection/>

      {/* Games Grid immediately visible */}
      <main className="games-container" role="main">
        <h2 className="section-title">Choose Your Game</h2>
        <div className="games-grid" role="list" aria-live="polite">
          {filtered.length ? filtered.map(game => {
            const best = getBest(game);
            return (
              <Link key={game.name} to={game.path} className="game-box" role="listitem" tabIndex="0" aria-label={`Play ${game.name} - ${game.desc}`}
                onKeyDown={e=>onCardKeyDown(e, game.path)} onMouseEnter={e=>onCardMouseEnter(e, game)} onMouseLeave={onCardMouseLeave} onFocus={e=>onCardMouseEnter(e, game)} onBlur={onCardMouseLeave} onClick={()=>playUIClick(soundOn)}>
                <div className="game-glow" aria-hidden/>
                <div className="game-icon" aria-hidden>{game.icon}</div>
                <h3>{game.name}</h3>
                <p className="game-desc" aria-hidden>{game.desc}</p>
                <div className="card-meta">
                  <span className="difficulty">{game.difficulty}</span>
                  <span className="best-score" title={`Top: ${best.score}`}>🏆 {best.score}</span>
                </div>
                <span className="play-btn" aria-hidden>Play Now ▶</span>
              </Link>
            );
          }) : <p className="no-results">No games match your search or filter.</p>}
        </div>
      </main>

      {/* Extra text sections moved to bottom */}
      <HowToPlay/>
      <FeaturedGames/>
      <TipsSection/>
      <WhyGameHub/>

      {/* Hover preview */}
      {hoverPreview && (
        <aside className="hover-preview" style={{top:hoverPreview.coords.y,left:hoverPreview.coords.x}} role="dialog" aria-label={`Preview ${hoverPreview.game.name}`}>
          <div className="hp-icon">{hoverPreview.game.icon}</div>
          <div className="hp-body">
            <strong>{hoverPreview.game.name}</strong>
            <p>{hoverPreview.game.desc}</p>
            <div className="hp-stats">
              <span>Difficulty: {hoverPreview.game.difficulty}</span>
              <span>Category: {hoverPreview.game.category}</span>
              <span>Plays: {readBestScore(hoverPreview.game.name.replace(/\s+/g,"_").toLowerCase()).plays}</span>
            </div>
          </div>
        </aside>
      )}

      {/* Footer */}
      <footer className="home-footer">
        <p>&copy; {new Date().getFullYear()} <span className="glow-text">SamSoftix</span> — Built with ♥</p>
        <p className="credits">Hints: Use keyboard (Tab / Enter) to navigate. Toggle themes & sound at top-right.</p>
      </footer>
    </div>
  );
}
